"use client";

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useSession } from "next-auth/react";
import ProcessingOverlay from "@/components/ProcessingOverlay";
import AuthModal from "@/components/AuthModal";
import {
  composeFreePfp,
  downloadFreePfp,
  isValidPfpUpload,
  pollLandingCutout,
  removeBackgroundFromFile,
} from "./freePfpExport";
import { landingDemoSrc, type LandingDemoId } from "@/libs/landingHandoff";
import {
  trackFreePfpDownloaded,
  trackFreePfpDownloadDelivered,
  trackFreePfpGateAuthCompleted,
  trackFreePfpGateAuthStarted,
  trackFreePfpGateShown,
  trackFreePfpOpened,
  trackFreePfpPollFailed,
  trackFreePfpReady,
  trackFreePfpReupload,
  type FreePfpHandoffSource,
} from "./freePfpAnalytics";
import {
  clearFreePfpTrayStash,
  clearGateDelivering,
  consumeJustAuthenticated,
  FREE_PFP_DOWNLOAD_AUTH_TRIGGER,
  isGateDelivering,
  markGateDelivering,
  readFreePfpTrayStash,
  writeFreePfpTrayStash,
  type FreePfpTrayStash,
} from "./freePfpTrayStash";
import { ColorPickerTrigger, SketchColorPopover } from "./FreePfpColorPicker";
import {
  buildFreePfpResults,
  FREE_PFP_DEMO_CUTOUT,
  FREE_PFP_PHOTOS,
  FREE_PFP_SWATCHES,
  isPresetSolidFill,
  type FreePfpResult,
  type FreePfpSwatch,
} from "./freePfpTrayData";

const SCALE_MIN = 0.65;
const SCALE_MAX = 1.85;
const ROTATE_MIN = -180;
const ROTATE_MAX = 180;
const TRANSLATE_MAX = 40;
/** Per-PFP download chips — hidden while the rail Download CTA is the only path. Flip on to restore. */
const SHOW_PFP_DOWNLOAD_MARKS = false;
/** Session-only expand/collapse; tray defaults closed each new visit. */
const COLLAPSE_STORAGE_KEY = "pfp_free_tray_collapsed";

function readTrayCollapsed(): boolean {
  try {
    const stored = sessionStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (stored === "0") return false;
    return true;
  } catch {
    return true;
  }
}

function writeTrayCollapsed(collapsed: boolean) {
  try {
    sessionStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    /* private mode / disabled storage */
  }
}

type ToolId = "looks" | "color" | "position";
type ColorLayer = "solid" | "gradient" | "photos";
type PfpIconName = "presets" | "background" | "position" | "download" | "change" | "reset";

const PFP_ICONS: Record<PfpIconName, string> = {
  presets: "/assets/icons/pfp-styles.svg",
  background: "/assets/icons/pfp-bg.svg",
  position: "/assets/icons/pfp-position.svg",
  download: "/assets/icons/pfp-download.svg",
  change: "/assets/icons/pfp-change.svg",
  reset: "/assets/icons/pfp-reset.svg",
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

/** Tints landing `pfp-*` glyphs with `currentColor` so tab/rail states still apply. */
function PfpIcon({ name, className }: { name: PfpIconName; className?: string }) {
  return (
    <span
      aria-hidden
      className={`inline-block shrink-0 ${className ?? ""}`}
      style={{
        backgroundColor: "currentColor",
        WebkitMask: `url(${PFP_ICONS[name]}) center / contain no-repeat`,
        mask: `url(${PFP_ICONS[name]}) center / contain no-repeat`,
      }}
    />
  );
}

function IconFlipH({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16M8 8L4 12l4 4M16 8l4 4-4 4" />
    </svg>
  );
}

function IconFlipV({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h16M8 8l4-4 4 4M8 16l4 4 4-4" />
    </svg>
  );
}

function IconChevronY({
  up,
  className,
}: {
  up?: boolean;
  className?: string;
}) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
      {up ? (
        <path
          fillRule="evenodd"
          d="M14.78 12.78a.75.75 0 01-1.06 0L10 9.06l-3.72 3.72a.75.75 0 11-1.06-1.06l4.25-4.25a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06z"
          clipRule="evenodd"
        />
      ) : (
        <path
          fillRule="evenodd"
          d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z"
          clipRule="evenodd"
        />
      )}
    </svg>
  );
}

function IconChevron({
  dir,
  className,
}: {
  dir: "left" | "right";
  className?: string;
}) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
      {dir === "left" ? (
        <path
          fillRule="evenodd"
          d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
          clipRule="evenodd"
        />
      ) : (
        <path
          fillRule="evenodd"
          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
          clipRule="evenodd"
        />
      )}
    </svg>
  );
}

function cutoutTransform(
  offsetX: number,
  offsetY: number,
  scale: number,
  rotate: number,
  flipH: boolean,
  flipV: boolean
) {
  return `translate(${offsetX}%, ${offsetY}%) scale(${scale}) rotate(${rotate}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`;
}

function FadeScroller({
  children,
  className = "",
  scrollerRef,
}: {
  children: React.ReactNode;
  className?: string;
  scrollerRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const localRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);
  const dragRef = useRef<{
    x: number;
    left: number;
    moved: boolean;
    captured: boolean;
    id: number;
  } | null>(null);
  const skipClickRef = useRef(false);
  const fadeRef = useRef({ left: false, right: false });
  const rafRef = useRef(0);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (scrollerRef) {
        (scrollerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [scrollerRef]
  );

  const measure = useCallback(() => {
    const el = localRef.current;
    if (!el) return;
    const nextLeft = el.scrollLeft > 6;
    const nextRight = el.scrollWidth - el.clientWidth - el.scrollLeft > 6;
    if (fadeRef.current.left === nextLeft && fadeRef.current.right === nextRight) return;
    fadeRef.current = { left: nextLeft, right: nextRight };
    setFadeLeft(nextLeft);
    setFadeRight(nextRight);
  }, []);

  const measureSoon = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      measure();
    });
  }, [measure]);

  const scrollByDir = (dir: "left" | "right") => {
    const el = localRef.current;
    if (!el) return;
    const amount = Math.max(140, el.clientWidth * 0.55);
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  useLayoutEffect(() => {
    measure();
    const el = localRef.current;
    if (!el) return;
    el.addEventListener("scroll", measureSoon, { passive: true });
    window.addEventListener("resize", measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measureSoon);
      window.removeEventListener("resize", measure);
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [measure, measureSoon, children]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("input[type=range]")) return;
    const el = localRef.current;
    if (!el) return;
    dragRef.current = {
      x: e.clientX,
      left: el.scrollLeft,
      moved: false,
      captured: false,
      id: e.pointerId,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const el = localRef.current;
    if (!drag || !el || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 10) return;
    if (!drag.captured) {
      drag.captured = true;
      drag.moved = true;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* capture is best-effort */
      }
      el.style.cursor = "grabbing";
    }
    el.scrollLeft = drag.left - dx;
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const el = localRef.current;
    if (!drag || e.pointerId !== drag.id) return;
    if (drag.moved) skipClickRef.current = true;
    if (el) el.style.cursor = "";
    if (drag.captured) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    }
    dragRef.current = null;
  };

  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!skipClickRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    skipClickRef.current = false;
  };

  return (
    <div className="relative h-full min-h-0">
      <div
        ref={setRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
        className={`flex h-full flex-nowrap items-center gap-2.5 overflow-x-auto overflow-y-hidden overscroll-x-contain select-none px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:cursor-grab ${className}`}
      >
        {children}
      </div>
      <div
        aria-hidden={!fadeLeft}
        className={`pointer-events-none absolute inset-y-0 left-0 z-10 w-11 bg-gradient-to-r from-white via-white/85 to-transparent transition-opacity ${
          fadeLeft ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        aria-hidden={!fadeRight}
        className={`pointer-events-none absolute inset-y-0 right-0 z-10 w-11 bg-gradient-to-l from-white via-white/85 to-transparent transition-opacity ${
          fadeRight ? "opacity-100" : "opacity-0"
        }`}
      />
      {fadeLeft && (
        <button
          type="button"
          aria-label="Scroll left"
          onClick={() => scrollByDir("left")}
          className="hidden md:flex absolute left-1 top-1/2 z-20 -translate-y-1/2 w-7 h-7 items-center justify-center rounded-full bg-white text-gray-500 shadow-[0_1px_6px_rgba(16,24,40,0.12)] border border-gray-100 hover:text-gray-800"
        >
          <IconChevron dir="left" className="w-3.5 h-3.5" />
        </button>
      )}
      {fadeRight && (
        <button
          type="button"
          aria-label="Scroll right"
          onClick={() => scrollByDir("right")}
          className="hidden md:flex absolute right-1 top-1/2 z-20 -translate-y-1/2 w-7 h-7 items-center justify-center rounded-full bg-white text-gray-500 shadow-[0_1px_6px_rgba(16,24,40,0.12)] border border-gray-100 hover:text-gray-800"
        >
          <IconChevron dir="right" className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

const PfpDisc = React.memo(function PfpDisc({
  result,
  cutoutUrl,
  offsetX,
  offsetY,
  scale,
  rotate = 0,
  flipH = false,
  flipV = false,
  cutoutRef,
}: {
  result: FreePfpResult;
  cutoutUrl: string;
  offsetX: number;
  offsetY: number;
  scale: number;
  rotate?: number;
  flipH?: boolean;
  flipV?: boolean;
  cutoutRef?: React.Ref<HTMLImageElement>;
}) {
  return (
    <span className="absolute inset-0 rounded-full overflow-hidden bg-gray-200">
      {result.kind === "photo" && result.backgroundUrl ? (
        <img src={result.backgroundUrl} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      ) : (
        <span className="absolute inset-0" style={{ background: result.fill || "#e5e7eb" }} />
      )}
      <img
        ref={cutoutRef}
        src={cutoutUrl}
        alt=""
        className={`absolute inset-0 w-full h-full object-contain pointer-events-none ${
          cutoutRef ? "will-change-transform" : ""
        }`}
        style={{
          transform: cutoutTransform(offsetX, offsetY, scale, rotate, flipH, flipV),
          transformOrigin: "center center",
        }}
        draggable={false}
      />
    </span>
  );
});

function DownloadMark({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`rounded-full bg-[#69C85A] border-[2.5px] border-white shadow-[0_2px_8px_rgba(16,24,40,0.18)] flex items-center justify-center pointer-events-none ${className}`}
    >
      <PfpIcon name="download" className="w-4 h-4 text-white" />
    </span>
  );
}

export default function FreePfpTray({
  landingDemoId = null,
  landingPpUid = null,
  landingHandoffSource = null,
}: {
  landingDemoId?: LandingDemoId | null;
  landingPpUid?: string | null;
  landingHandoffSource?: FreePfpHandoffSource | null;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const presets = useMemo(() => buildFreePfpResults(), []);
  const { data: session, status: sessionStatus } = useSession();
  const isAuthenticated = sessionStatus === "authenticated" && !!session?.user;
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [active, setActive] = useState<FreePfpResult>(() => ({ ...buildFreePfpResults()[0] }));
  const [presetId, setPresetId] = useState(() => buildFreePfpResults()[0].id);
  const [cutoutUrl, setCutoutUrl] = useState(() =>
    landingDemoId ? landingDemoSrc(landingDemoId) : FREE_PFP_DEMO_CUTOUT
  );
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [activeTool, setActiveTool] = useState<ToolId>("looks");
  const [strip, setStrip] = useState<"looks" | "items" | "position">("looks");
  const [colorLayer, setColorLayer] = useState<ColorLayer>("solid");
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerAnchorRef = useRef<HTMLButtonElement>(null);
  const [collapsed, setCollapsed] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const busyRef = useRef(false);
  const handoffOpenedKey = useRef<string | null>(null);
  const handoffReadyKey = useRef<string | null>(null);
  const skipHandoffHydrateRef = useRef(false);
  const pendingGateDownloadRef = useRef<FreePfpTrayStash | null>(null);
  const gateDeliveredRef = useRef(false);
  const circleRef = useRef<HTMLDivElement>(null);
  const cutoutImgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number; id: number } | null>(null);
  const poseRef = useRef({ offsetX, offsetY, scale, rotate, flipH, flipV });
  poseRef.current = { offsetX, offsetY, scale, rotate, flipH, flipV };

  const paintCutout = useCallback(() => {
    const el = cutoutImgRef.current;
    if (!el) return;
    const p = poseRef.current;
    el.style.transform = cutoutTransform(p.offsetX, p.offsetY, p.scale, p.rotate, p.flipH, p.flipV);
  }, []);

  const solids = FREE_PFP_SWATCHES.filter((s) => s.kind === "color");
  const gradients = FREE_PFP_SWATCHES.filter((s) => s.kind === "gradient");
  const photoLooks = FREE_PFP_PHOTOS;
  const customSolidActive =
    active.kind === "color" && !!active.fill && !isPresetSolidFill(active.fill);

  const openColorMenu = () => {
    setPickerOpen(false);
    setActiveTool("color");
    setColorMenuOpen(true);
  };

  const onColorTab = () => {
    if (colorMenuOpen) {
      setColorMenuOpen(false);
      if (strip !== "items") setActiveTool("looks");
      return;
    }
    openColorMenu();
  };

  const pickColorLayer = (layer: ColorLayer) => {
    setColorLayer(layer);
    setColorMenuOpen(false);
    setPickerOpen(false);
    setStrip("items");
    setActiveTool("color");
  };

  const showLooks = () => {
    setActiveTool("looks");
    setStrip("looks");
    setColorMenuOpen(false);
    setPickerOpen(false);
  };

  const showPosition = () => {
    setActiveTool("position");
    setStrip("position");
    setColorMenuOpen(false);
    setPickerOpen(false);
  };

  const applyPreset = (preset: FreePfpResult) => {
    setPickerOpen(false);
    setPresetId(preset.id);
    setActive({ ...preset });
  };

  const applySwatch = (swatch: FreePfpSwatch) => {
    setPickerOpen(false);
    setActive((prev) => ({
      ...prev,
      kind: swatch.kind,
      fill: swatch.fill,
      backgroundUrl: undefined,
    }));
  };

  const applyCustomColor = (hex: string) => {
    setActive((prev) => ({
      ...prev,
      kind: "color",
      fill: hex,
      backgroundUrl: undefined,
    }));
  };

  const applyPhoto = (preset: FreePfpResult) => {
    setPickerOpen(false);
    setActive((prev) => ({
      ...prev,
      kind: "photo",
      fill: undefined,
      backgroundUrl: preset.backgroundUrl,
    }));
  };

  const resetPose = () => {
    setOffsetX(0);
    setOffsetY(0);
    setScale(1);
    setRotate(0);
    setFlipH(false);
    setFlipV(false);
    poseRef.current = { offsetX: 0, offsetY: 0, scale: 1, rotate: 0, flipH: false, flipV: false };
  };

  const applyStash = useCallback((stash: FreePfpTrayStash) => {
    setCutoutUrl(stash.cutoutUrl);
    setActive({ ...stash.active });
    setPresetId(stash.presetId);
    setOffsetX(stash.offsetX);
    setOffsetY(stash.offsetY);
    setScale(stash.scale);
    setRotate(stash.rotate);
    setFlipH(stash.flipH);
    setFlipV(stash.flipV);
    poseRef.current = {
      offsetX: stash.offsetX,
      offsetY: stash.offsetY,
      scale: stash.scale,
      rotate: stash.rotate,
      flipH: stash.flipH,
      flipV: stash.flipV,
    };
    setCollapsed(false);
    writeTrayCollapsed(false);
  }, []);

  const runDownload = useCallback(
    async (opts?: { stash?: FreePfpTrayStash; afterGate?: boolean }) => {
      if (busyRef.current || isProcessing) return;
      busyRef.current = true;
      setIsDownloading(true);
      if (opts?.afterGate) markGateDelivering();
      try {
        const pose = opts?.stash
          ? {
              offsetX: opts.stash.offsetX,
              offsetY: opts.stash.offsetY,
              scale: opts.stash.scale,
              rotate: opts.stash.rotate,
              flipH: opts.stash.flipH,
              flipV: opts.stash.flipV,
            }
          : poseRef.current;
        const result = opts?.stash?.active ?? active;
        const imageData = await composeFreePfp({
          cutoutUrl: opts?.stash?.cutoutUrl ?? cutoutUrl,
          fill: result.fill,
          backgroundUrl: result.kind === "photo" ? result.backgroundUrl : undefined,
          offsetX: pose.offsetX,
          offsetY: pose.offsetY,
          scale: pose.scale,
          rotate: pose.rotate,
          flipH: pose.flipH,
          flipV: pose.flipV,
        });
        await downloadFreePfp(imageData, `pfpmaker-pfp-${Date.now()}.png`);
        const source = landingHandoffSource ?? "in_app";
        trackFreePfpDownloaded({ source });
        if (opts?.afterGate) {
          trackFreePfpDownloadDelivered({ source, afterGate: true });
          clearFreePfpTrayStash();
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Download failed. Please try again.");
      } finally {
        if (opts?.afterGate) clearGateDelivering();
        busyRef.current = false;
        setIsDownloading(false);
      }
    },
    [active, cutoutUrl, isProcessing, landingHandoffSource]
  );

  const onReupload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isValidPfpUpload(file)) {
      setProcessError("Use a PNG, JPG, or WEBP photo");
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setIsProcessing(true);
    setProcessError(null);
    try {
      const cutout = await removeBackgroundFromFile(file);
      setCutoutUrl(cutout);
      resetPose();
      trackFreePfpReupload({ ok: true });
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : "Couldn't remove the background");
      trackFreePfpReupload({
        ok: false,
        error: err instanceof Error ? err.message : "Couldn't remove the background",
      });
    } finally {
      busyRef.current = false;
      setIsProcessing(false);
    }
  };

  const onDownload = async () => {
    if (busyRef.current || isProcessing || isDownloading) return;
    if (sessionStatus === "loading") return;
    if (!isAuthenticated) {
      const saved = writeFreePfpTrayStash({
        cutoutUrl,
        active: { ...active },
        presetId,
        offsetX: poseRef.current.offsetX,
        offsetY: poseRef.current.offsetY,
        scale: poseRef.current.scale,
        rotate: poseRef.current.rotate,
        flipH: poseRef.current.flipH,
        flipV: poseRef.current.flipV,
      });
      if (!saved) {
        toast.error("Couldn't save your preview. After signing in you may need to re-upload.");
      }
      trackFreePfpGateShown();
      setShowAuthModal(true);
      return;
    }
    await runDownload();
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isProcessing || processError) return;
    if (e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort; move/up still track via pointerId */
    }
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: poseRef.current.offsetX,
      oy: poseRef.current.offsetY,
      id: e.pointerId,
    };
    e.currentTarget.style.cursor = "grabbing";
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || e.pointerId !== drag.id) return;
    const size = circleRef.current?.clientWidth || 1;
    const dx = ((e.clientX - drag.x) / size) * 100;
    const dy = ((e.clientY - drag.y) / size) * 100;
    poseRef.current.offsetX = clamp(drag.ox + dx, -TRANSLATE_MAX, TRANSLATE_MAX);
    poseRef.current.offsetY = clamp(drag.oy + dy, -TRANSLATE_MAX, TRANSLATE_MAX);
    paintCutout();
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.id !== e.pointerId) return;
    dragRef.current = null;
    e.currentTarget.style.cursor = "";
    setOffsetX(poseRef.current.offsetX);
    setOffsetY(poseRef.current.offsetY);
    setDragging(false);
  };

  useEffect(() => {
    const el = optionsRef.current;
    if (!el || strip !== "looks" || colorMenuOpen) return;
    const child = [...el.querySelectorAll("[data-preset-id]")].find(
      (node) => (node as HTMLElement).dataset.presetId === presetId
    ) as HTMLElement | undefined;
    child?.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
  }, [presetId, strip, colorMenuOpen]);

  useEffect(() => {
    setCollapsed(readTrayCollapsed());
  }, []);

  useLayoutEffect(() => {
    const stash = readFreePfpTrayStash();
    if (!stash) return;
    skipHandoffHydrateRef.current = true;
    applyStash(stash);
    if (consumeJustAuthenticated()) {
      pendingGateDownloadRef.current = stash;
      trackFreePfpGateAuthCompleted();
    }
  }, [applyStash]);

  useEffect(() => {
    if (gateDeliveredRef.current) return;
    const pending = pendingGateDownloadRef.current;
    if (!pending && isGateDelivering()) {
      gateDeliveredRef.current = true;
      return;
    }
    if (pending) {
      gateDeliveredRef.current = true;
      pendingGateDownloadRef.current = null;
      void runDownload({ stash: pending, afterGate: true });
      return;
    }
    if (sessionStatus !== "authenticated") return;
    const stash = readFreePfpTrayStash();
    if (!stash) return;
    gateDeliveredRef.current = true;
    trackFreePfpGateAuthCompleted();
    void runDownload({ stash, afterGate: true });
  }, [sessionStatus, runDownload]);

  useEffect(() => {
    if (skipHandoffHydrateRef.current) return;
    if (!landingDemoId) return;
    setCutoutUrl(landingDemoSrc(landingDemoId));
    resetPose();
    setCollapsed(false);
    writeTrayCollapsed(false);
    setIsProcessing(false);
    setProcessError(null);
    const key = `demo:${landingDemoId}`;
    if (handoffOpenedKey.current !== key) {
      handoffOpenedKey.current = key;
      trackFreePfpOpened({ source: "demo", demoId: landingDemoId });
    }
    if (handoffReadyKey.current !== key) {
      handoffReadyKey.current = key;
      trackFreePfpReady({ source: "demo", demoId: landingDemoId });
    }
  }, [landingDemoId]);

  useEffect(() => {
    if (skipHandoffHydrateRef.current) return;
    if (landingDemoId || !landingPpUid) return;
    const key = `upload:${landingPpUid}`;
    if (handoffOpenedKey.current !== key) {
      handoffOpenedKey.current = key;
      trackFreePfpOpened({ source: "upload", ppUid: landingPpUid });
    }
    let cancelled = false;
    busyRef.current = true;
    setIsProcessing(true);
    setProcessError(null);
    setCollapsed(false);
    writeTrayCollapsed(false);
    (async () => {
      try {
        const cutout = await pollLandingCutout(landingPpUid);
        if (cancelled) return;
        setCutoutUrl(cutout);
        resetPose();
        if (handoffReadyKey.current !== key) {
          handoffReadyKey.current = key;
          trackFreePfpReady({ source: "upload" });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Couldn't load your photo";
          setProcessError(message);
          trackFreePfpPollFailed({ error: message });
        }
      } finally {
        if (!cancelled) {
          busyRef.current = false;
          setIsProcessing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      busyRef.current = false;
    };
  }, [landingDemoId, landingPpUid]);

  useEffect(() => {
    if (skipHandoffHydrateRef.current) return;
    if (landingHandoffSource !== "fallback") return;
    const key = "fallback";
    if (handoffOpenedKey.current !== key) {
      handoffOpenedKey.current = key;
      trackFreePfpOpened({ source: "fallback" });
    }
    if (handoffReadyKey.current !== key) {
      handoffReadyKey.current = key;
      trackFreePfpReady({ source: "fallback", defaultCutout: true });
    }
  }, [landingHandoffSource]);

  useEffect(() => {
    if (skipHandoffHydrateRef.current) return;
    if (landingDemoId || landingPpUid || landingHandoffSource) return;
    const key = "in_app";
    if (handoffOpenedKey.current !== key) {
      handoffOpenedKey.current = key;
      trackFreePfpOpened({ source: "in_app" });
    }
    if (handoffReadyKey.current !== key) {
      handoffReadyKey.current = key;
      trackFreePfpReady({ source: "in_app", defaultCutout: true });
    }
  }, [landingDemoId, landingPpUid, landingHandoffSource]);

  const collapseTray = () => {
    setPickerOpen(false);
    setCollapsed(true);
    writeTrayCollapsed(true);
  };

  const expandTray = () => {
    setCollapsed(false);
    writeTrayCollapsed(false);
  };

  useEffect(() => {
    if (!colorMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setColorMenuOpen(false);
        if (strip !== "items") setActiveTool("looks");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [colorMenuOpen, strip]);

  return (
    <section className="w-full max-w-xl mx-auto flex flex-col items-center">
      {collapsed ? (
        <button
          type="button"
          onClick={expandTray}
          aria-expanded="false"
          aria-controls="free-pfp-tray-body"
          title="Show free profile pictures"
          className="w-full flex items-center gap-3 rounded-2xl bg-white px-3 py-2 text-left [filter:drop-shadow(0_4px_24px_rgba(16,24,40,0.06))] hover:[filter:drop-shadow(0_6px_28px_rgba(16,24,40,0.1))] transition-[filter] duration-300"
        >
          <span className="relative w-9 h-9 rounded-full overflow-hidden shrink-0">
            <PfpDisc
              result={active}
              cutoutUrl={cutoutUrl}
              offsetX={offsetX}
              offsetY={offsetY}
              scale={scale}
              rotate={rotate}
              flipH={flipH}
              flipV={flipV}
            />
          </span>
          <span
            className="min-w-0 flex-1 text-sm md:text-base font-bold text-gray-800 truncate"
            style={{ fontFamily: "var(--font-merriweather)" }}
          >
            Free profile picture maker
          </span>
          <span className="bg-[#69C85A] text-white text-[8px] font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap">
            FREE
          </span>
          <IconChevronY className="w-4 h-4 text-gray-400 shrink-0" />
        </button>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 w-full mb-3 md:mb-4">
          <div />
          <div className="flex items-center justify-center gap-3">
            <h2
              className="text-xl md:text-2xl font-bold text-gray-800"
              style={{ fontFamily: "var(--font-merriweather)" }}
            >
              Free profile picture maker
            </h2>
          </div>
          <button
            type="button"
            onClick={collapseTray}
            aria-expanded="true"
            aria-controls="free-pfp-tray-body"
            title="Hide free profile pictures"
            className="justify-self-end flex items-center gap-0.5 text-[11px] font-semibold text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="hidden sm:inline">Hide</span>
            <IconChevronY up className="w-4 h-4" />
          </button>
        </div>
      )}

      <div
        id="free-pfp-tray-body"
        className={collapsed ? "hidden" : "w-full flex flex-col items-center"}
      >
      {/* Preview + compact right rail */}
      <div className="relative flex items-center gap-2">
        <div className="w-9 shrink-0" aria-hidden="true" />
        <div className="relative shrink-0">
          <div
            ref={circleRef}
            role="img"
            aria-label="Adjust photo position"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className={`relative w-[168px] h-[168px] sm:w-[184px] sm:h-[184px] md:w-[220px] md:h-[220px] rounded-full overflow-hidden touch-none select-none ${
              isProcessing || processError
                ? "cursor-default"
                : dragging
                  ? "cursor-grabbing"
                  : "cursor-grab"
            }`}
          >
            <PfpDisc
              result={active}
              cutoutUrl={cutoutUrl}
              offsetX={offsetX}
              offsetY={offsetY}
              scale={scale}
              rotate={rotate}
              flipH={flipH}
              flipV={flipV}
              cutoutRef={cutoutImgRef}
            />
            <ProcessingOverlay
              isProcessing={isProcessing}
              error={processError}
              compact
              customMessage="Removing background..."
              customWaitTime="5-15"
              onErrorDismiss={() => {
                setProcessError(null);
                fileRef.current?.click();
              }}
            />
          </div>
          {SHOW_PFP_DOWNLOAD_MARKS && (
            <DownloadMark className="absolute bottom-1.5 right-1.5 w-9 h-9" />
          )}
        </div>
        <div className="flex flex-col gap-2 w-9 shrink-0">
          <RailBtn
            label={
              isDownloading
                ? "Downloading"
                : isAuthenticated
                  ? "Download"
                  : "Save & download"
            }
            tone="primary"
            disabled={isProcessing || isDownloading}
            onClick={onDownload}
          >
            {isDownloading ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path className="opacity-75" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M12 2a10 10 0 0110 10" />
              </svg>
            ) : (
              <PfpIcon name="download" className="w-3.5 h-3.5" />
            )}
          </RailBtn>
          <RailBtn
            label="Replace photo"
            disabled={isProcessing || isDownloading}
            onClick={() => fileRef.current?.click()}
          >
            <PfpIcon name="change" className="w-3.5 h-3.5" />
          </RailBtn>
        </div>
      </div>

      {/* T-shaped card: wide settings pane, compact tab foot hanging from center */}
      <div className="relative w-full mt-3 flex flex-col items-center [filter:drop-shadow(0_4px_24px_rgba(16,24,40,0.06))] hover:[filter:drop-shadow(0_6px_28px_rgba(16,24,40,0.1))] transition-[filter] duration-300 ease-out">
        <div className="relative w-full rounded-2xl bg-white overflow-hidden">
        <div
          className={`relative ${
            strip === "position" ? "h-[92px]" : "h-[65px] md:h-[70px]"
          }`}
        >
          {strip === "looks" && (
            <FadeScroller scrollerRef={optionsRef}>
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  data-preset-id={preset.id}
                  onClick={() => applyPreset(preset)}
                  aria-pressed={preset.id === presetId}
                  aria-label={preset.label}
                  title={preset.label}
                  className="relative shrink-0 rounded-full appearance-none bg-transparent border-0 p-0.5 outline-none ring-2 ring-transparent hover:ring-primary focus-visible:ring-primary transition-[box-shadow] duration-200"
                >
                  <span className="relative block w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden">
                    <PfpDisc result={preset} cutoutUrl={cutoutUrl} offsetX={0} offsetY={0} scale={1} />
                  </span>
                </button>
              ))}
            </FadeScroller>
          )}

          {strip === "items" && (
            <FadeScroller key={`items-${colorLayer}`}>
              {colorLayer === "solid" && (
                <>
                  <ColorPickerTrigger
                    selected={customSolidActive || pickerOpen}
                    buttonRef={pickerAnchorRef}
                    onClick={() => setPickerOpen((open) => !open)}
                  />
                  {solids.map((swatch) => {
                    const selected =
                      active.kind === "color" &&
                      !!active.fill &&
                      active.fill.toLowerCase() === swatch.fill.toLowerCase();
                    return (
                      <button
                        key={swatch.id}
                        type="button"
                        aria-label={swatch.label}
                        title={swatch.label}
                        aria-pressed={selected}
                        onClick={() => applySwatch(swatch)}
                        className={`relative shrink-0 w-12 h-12 md:w-12 md:h-12 rounded-full border border-black/10 shadow-sm ring-2 hover:ring-primary focus-visible:ring-primary transition-[box-shadow] duration-200 ${
                          selected ? "ring-primary" : "ring-transparent"
                        }`}
                        style={{ background: swatch.fill }}
                      />
                    );
                  })}
                </>
              )}
              {colorLayer === "gradient" &&
                gradients.map((swatch) => {
                  const selected = active.kind === "gradient" && active.fill === swatch.fill;
                  return (
                    <button
                      key={swatch.id}
                      type="button"
                      aria-label={swatch.label}
                      title={swatch.label}
                      aria-pressed={selected}
                      onClick={() => applySwatch(swatch)}
                      className={`relative shrink-0 w-12 h-12 md:w-12 md:h-12 rounded-full border border-black/10 shadow-sm ring-2 hover:ring-primary focus-visible:ring-primary transition-[box-shadow] duration-200 ${
                        selected ? "ring-primary" : "ring-transparent"
                      }`}
                      style={{ background: swatch.fill }}
                    />
                  );
                })}
              {colorLayer === "photos" &&
                photoLooks.map((preset) => {
                  const selected = active.kind === "photo" && active.backgroundUrl === preset.backgroundUrl;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      aria-label={preset.label}
                      title={preset.label}
                      aria-pressed={selected}
                      onClick={() => applyPhoto(preset)}
                      className={`relative shrink-0 rounded-full appearance-none bg-transparent border-0 p-0.5 outline-none ring-2 hover:ring-primary focus-visible:ring-primary transition-[box-shadow] duration-200 ${
                        selected ? "ring-primary" : "ring-transparent"
                      }`}
                    >
                      <span className="relative block w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden">
                        <PfpDisc result={preset} cutoutUrl={cutoutUrl} offsetX={0} offsetY={0} scale={1} />
                      </span>
                    </button>
                  );
                })}
            </FadeScroller>
          )}

          {strip === "position" && (
            <div className="flex h-full items-stretch gap-2 px-3 py-1.5">
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
                <MiniSlider
                  label="Zoom"
                  min={SCALE_MIN}
                  max={SCALE_MAX}
                  step={0.01}
                  value={scale}
                  defaultValue={1}
                  onChange={setScale}
                />
                <MiniSlider
                  label="Rotate"
                  min={ROTATE_MIN}
                  max={ROTATE_MAX}
                  step={1}
                  value={rotate}
                  defaultValue={0}
                  onChange={setRotate}
                />
              </div>
              <div className="flex shrink-0 flex-col justify-center gap-1">
                <button
                  type="button"
                  aria-label="Flip horizontal"
                  aria-pressed={flipH}
                  onClick={() => setFlipH((v) => !v)}
                  className={`w-9 flex-1 rounded-xl flex items-center justify-center ${
                    flipH ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <IconFlipH className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  aria-label="Flip vertical"
                  aria-pressed={flipV}
                  onClick={() => setFlipV((v) => !v)}
                  className={`w-9 flex-1 rounded-xl flex items-center justify-center ${
                    flipV ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <IconFlipV className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {colorMenuOpen && (
            <div
              className="absolute inset-0 z-30 overflow-hidden animate-fade-in"
              onClick={() => {
                setColorMenuOpen(false);
                if (strip !== "items") setActiveTool("looks");
              }}
            >
              <div className="absolute inset-0 bg-white/55 backdrop-blur-md" aria-hidden="true" />
              <div
                className="relative z-10 h-full flex items-center justify-center gap-2 px-3"
                onClick={(e) => e.stopPropagation()}
              >
                <ColorMenuChoice
                  label="Solid"
                  onClick={() => pickColorLayer("solid")}
                />
                <ColorMenuChoice
                  label="Gradient"
                  onClick={() => pickColorLayer("gradient")}
                />
                <ColorMenuChoice
                  label="Photos"
                  onClick={() => pickColorLayer("photos")}
                />
              </div>
            </div>
          )}
        </div>
        </div>

        <div className="relative z-10 -mt-px inline-flex items-stretch bg-gray-50 rounded-b-xl overflow-hidden">
          <ToolTab
            label="Presets"
            active={activeTool === "looks" && !colorMenuOpen}
            onClick={showLooks}
            icon={<PfpIcon name="presets" className="w-4 h-4" />}
          />
          <ToolTab
            label="Background"
            active={activeTool === "color"}
            onClick={onColorTab}
            icon={<PfpIcon name="background" className="w-4 h-4" />}
          />
          <ToolTab
            label="Position"
            active={activeTool === "position"}
            onClick={showPosition}
            icon={<PfpIcon name="position" className="w-4 h-4" />}
          />
        </div>
      </div>

      <a
        href="#studio-looks"
        className="mt-8 flex flex-col items-center text-primary hover:text-primary/80 transition-colors"
      >
        <span className="text-[11px] md:text-xs font-semibold tracking-[0.14em] uppercase">
          Need more? Try 1000+ AI styles below
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4 mt-0.5 motion-safe:animate-bounce"
        >
          <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clipRule="evenodd" />
        </svg>
      </a>
      </div>

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={onReupload} />
      {pickerOpen && strip === "items" && colorLayer === "solid" && (
        <SketchColorPopover
          anchorRef={pickerAnchorRef}
          value={
            active.kind === "color" && active.fill && !active.fill.includes("gradient")
              ? active.fill
              : "#5956E9"
          }
          onChange={applyCustomColor}
          onClose={() => setPickerOpen(false)}
        />
      )}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {}}
        callbackUrl="/dashboard"
        headline="Save it to your account"
        subheadline="Sign in to download your profile picture in HD"
        trigger={FREE_PFP_DOWNLOAD_AUTH_TRIGGER}
        onAuthStarted={(method) => trackFreePfpGateAuthStarted({ method })}
      />
    </section>
  );
}

function RailBtn({
  label,
  children,
  onClick,
  tone = "muted",
  disabled = false,
}: {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  tone?: "primary" | "muted";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-[0_2px_8px_rgba(16,24,40,0.08)] disabled:opacity-50 disabled:pointer-events-none ${
        tone === "primary"
          ? "bg-[#69C85A] text-white hover:bg-[#5db84f]"
          : "bg-white text-gray-700 border border-gray-100 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

function ColorMenuChoice({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 px-4 rounded-full text-xs font-semibold bg-white text-gray-700 border border-gray-200 shadow-sm hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors duration-200"
    >
      {label}
    </button>
  );
}

function ToolTab({
  label,
  icon,
  onClick,
  active = false,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`relative flex flex-col items-center gap-0.5 px-6 py-1.5 transition-colors ${
        active
          ? "bg-white text-primary"
          : "text-gray-400 hover:text-gray-600"
      }`}
    >
      {active && (
        <span className="absolute inset-x-2 top-0 h-[2px] rounded-full bg-primary" aria-hidden="true" />
      )}
      {icon}
      <span className="text-[10px] font-semibold leading-none">{label}</span>
    </button>
  );
}

function MiniSlider({
  label,
  min,
  max,
  step,
  value,
  defaultValue,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  defaultValue: number;
  onChange: (n: number) => void;
}) {
  const isDefault = Math.abs(value - defaultValue) < step;
  return (
    <div className="flex items-center gap-2.5 min-w-0 flex-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 shrink-0">
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
        className="pfp-range flex-1 min-w-0 cursor-pointer touch-manipulation"
      />
      <button
        type="button"
        aria-label={`Reset ${label}`}
        title={`Reset ${label}`}
        disabled={isDefault}
        onClick={() => onChange(defaultValue)}
        className="shrink-0 h-7 rounded-lg flex items-center justify-center text-primary hover:bg-primary/10 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-default transition-colors"
      >
        <PfpIcon name="reset" className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
